<template>
  <v-card :loading="isSaving">
    <v-card-title class="headline font-weight-thin">Copy Governed Template</v-card-title>
    <v-card-text>
      Create a new release note from
      <span class="font-weight-bold">{{ releaseNote.title }} (ID: {{ releaseNote.id }})</span>.
      <v-text-field
        :hint="checkFailed ? 'Could not check for existing release notes. You can still copy.' : ''"
        :loading="isChecking"
        :rules="[(v) => !!(v && v.trim()) || 'QA Jira ID is required']"
        @blur="checkRelatedReleaseNotes"
        @keyup.enter="onEnter"
        class="mt-5"
        autofocus
        clearable
        data-cy="directCopy__input--qaJira"
        label="QA Jira ID *"
        outlined
        persistent-hint
        v-model="qaJira"
      ></v-text-field>
      <v-alert class="mb-6" data-cy="directCopy__warning" outlined type="warning" v-if="relatedReleaseNoteList.length">
        <div class="text--primary">
          <div class="font-weight-medium">This QA Jira already has a release note</div>
          {{ checkedQaJira }} is already linked to the following release note(s):
          <ul class="mt-2">
            <li v-for="related in relatedReleaseNoteList" :key="related.id">
              <router-link
                :to="{ name: 'releaseNoteView', params: { packId: related.packId, releaseNoteId: related.id } }"
                target="_blank"
              >
                {{ related.title }} (ID: {{ related.id }})
              </router-link>
            </li>
          </ul>
          <div class="mt-2">
            You can still continue and a new release note will be created. Review the existing note(s) first to avoid an
            unintended duplicate.
          </div>
        </div>
      </v-alert>
      <pack-selector
        :loading="packList.length === 0"
        :packList="packList"
        :dense="false"
        :outlined="true"
        :preselectRoutePack="false"
        @selectPack="selectPack"
        label="Destination Pack *"
        topMargin="0"
      ></pack-selector>
    </v-card-text>
    <v-card-actions class="pa-4">
      <v-spacer></v-spacer>
      <v-btn @click="cancel" class="mr-2" data-cy="directCopy__button--cancel" outlined>Cancel</v-btn>
      <v-btn :disabled="!canSubmit" @click="submit" color="primary" data-cy="directCopy__button--submit">Copy</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from "vuex";

import releaseNoteApi from "@/api/releaseNote";
import PackSelector from "@/components/PackSelector";

const CHECK_DELAY_MS = 400;

export default {
  components: { PackSelector },
  computed: {
    canSubmit() {
      return !!this.trimmedQaJira && !!this.destinationPackId && !this.isSaving && !this.isChecking;
    },
    trimmedQaJira() {
      return (this.qaJira || "").trim();
    },
    ...mapState({
      packList: (state) => state.pack.packList,
    }),
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
  },
  data() {
    return {
      checkedQaJira: "",
      checkFailed: false,
      debounceTimer: null,
      destinationPackId: null,
      isChecking: false,
      isSaving: false,
      latestCheckId: 0,
      qaJira: "",
      relatedReleaseNoteList: [],
      shownWarningFor: "",
    };
  },
  methods: {
    cancel() {
      this.$emit("setDialog", false);
    },
    // Looks up existing release notes for the QA Jira. Only the most recent check may update the dialog, so a
    // slow answer for an earlier value can never overwrite the result for what is typed now.
    async checkRelatedReleaseNotes() {
      clearTimeout(this.debounceTimer);
      const qaJira = this.trimmedQaJira;
      if (!qaJira) {
        this.resetCheck();
        return;
      }

      const checkId = ++this.latestCheckId;
      this.isChecking = true;

      let relatedReleaseNoteList = [];
      let checkFailed = false;
      try {
        relatedReleaseNoteList = await releaseNoteApi.getRelatedReleaseNotes(qaJira);
      } catch (e) {
        // The check is advisory, so a failure is shown but never blocks copying.
        console.error(e);
        checkFailed = true;
      }

      if (checkId !== this.latestCheckId) {
        return;
      }

      this.relatedReleaseNoteList = relatedReleaseNoteList;
      this.checkFailed = checkFailed;
      this.checkedQaJira = qaJira;
      if (relatedReleaseNoteList.length) {
        this.shownWarningFor = qaJira;
      }
      this.isChecking = false;
    },
    resetCheck() {
      clearTimeout(this.debounceTimer);
      this.latestCheckId += 1;
      this.relatedReleaseNoteList = [];
      this.checkedQaJira = "";
      this.checkFailed = false;
      this.isChecking = false;
    },
    selectPack(packId) {
      this.destinationPackId = packId;
    },
    onEnter() {
      if (this.canSubmit) {
        this.submit();
      } else {
        this.checkRelatedReleaseNotes();
      }
    },
    async submit() {
      const warningAlreadyShown = this.shownWarningFor === this.trimmedQaJira;
      this.isSaving = true;
      try {
        await this.checkRelatedReleaseNotes();
        if (this.relatedReleaseNoteList.length && !warningAlreadyShown) {
          // The duplicate warning has only just appeared; make the user see it before a second click copies.
          return;
        }

        const newReleaseNote = await releaseNoteApi.copyFromTemplate(this.releaseNote.id, {
          packId: this.destinationPackId,
          qaJira: this.trimmedQaJira,
        });
        this.$emit("setDialog", false);
        this.$notifySuccess({ message: `Created ${newReleaseNote.title} (ID: ${newReleaseNote.id})` });
        this.$router.push({
          name: "releaseNoteEdit",
          params: { packId: newReleaseNote.packId, releaseNoteId: newReleaseNote.id },
        });
      } catch (e) {
        this.$notifyError({ message: "Could not copy the governed template." });
      } finally {
        this.isSaving = false;
      }
    },
  },
  props: {
    releaseNote: { required: true, type: Object },
  },
  watch: {
    qaJira() {
      clearTimeout(this.debounceTimer);
      if (!this.trimmedQaJira) {
        this.resetCheck();
        return;
      }

      // Copy stays disabled from the first keystroke until the check for the final value has finished.
      this.isChecking = true;
      this.debounceTimer = setTimeout(() => this.checkRelatedReleaseNotes(), CHECK_DELAY_MS);
    },
  },
};
</script>
